import {
  CommonHelperFunctions,
  DashboardState,
  EScreenType,
  IWorkFlow,
  KeyCombinations,
  LeftPanelType,
  RightPanelType,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import * as React from "react";
import { connect } from "react-redux";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { isInsideModalOverlay } from "../../../shared/utility/modal-keyboard-scope";
import { showDomainAccessRequiredModal } from "../../../store/domain-access-modal/actions";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import { isReportDesignerEnabled } from "../../design-tools/report-designer/report-designer-feature-gate";

declare let window: any;
export interface IShortcutListenerForNavigationState {}
export interface IShortcutListenerForNavigationProps {
  setNextCallbackFunction: (cb?: () => void) => void;
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
}

class ShortcutListenerForNavigation extends React.PureComponent<
  IShortcutListenerForNavigationProps & IWithHistoryProp & IWithSDProps,
  IShortcutListenerForNavigationState
> {
  constructor(props: IShortcutListenerForNavigationProps & IWithHistoryProp & IWithSDProps) {
    super(props);
    this.state = {};
  }

  shortcuts = [
    { key: KeyCombinations.alt1, execute: () => this.handleRedirectUIDesign() },
    { key: KeyCombinations.alt2, execute: () => this.handleRedirectToDatasources() },
    { key: KeyCombinations.alt3, execute: () => this.handleRedirectToWorkflow() },
    { key: KeyCombinations.alt4, execute: () => this.handleRedirectToNotificationTemplate() },
    { key: KeyCombinations.alt5, execute: () => this.handleRedirectToReportDesigner() },
    { key: KeyCombinations.alt6, execute: () => this.handleRedirectToScheduledJobs() },
    { key: KeyCombinations.alt7, execute: () => this.handleRedirectToAPIBuilder() },
    { key: KeyCombinations.alt8, execute: () => this.handleRedirectToConfigurationManager() },
    { key: KeyCombinations.alt9, execute: () => this.handleRedirectToHelpDesk() }
  ];

  componentDidMount() {
    window.addEventListener("keydown", this.onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.onKeyDown);
  }

  private getIsEventPerformedForNonInputTarget = (e: any): boolean => {
    return (
      e.target?.tagName?.toUpperCase() === "DIV" ||
      e.target?.tagName?.toUpperCase() === "SPAN" ||
      e.target?.tagName?.toUpperCase() === "BODY"
    );
  };

  private getIsEventPerformedOnTextInputTarget = (e: any): boolean => {
    return (
      e.target?.tagName?.toUpperCase() === "INPUT" ||
      e.target?.tagName?.toUpperCase() === "TEXTAREA" ||
      (this.getIsEventPerformedForNonInputTarget(e) && e.target?.getAttribute("contenteditable") === "true")
    );
  };

  onKeyDown = (e: KeyboardEvent) => {
    const kc = CommonHelperFunctions.getKeyCombinationsFromKeysForDesignWindows(e);
    if (!kc) return;

    const isInMonacoEditor = (e as any).target?.closest?.(".monaco-editor") !== null;
    const isInModal: boolean = isInsideModalOverlay(e.target);

    if (
      e.target instanceof HTMLElement &&
      (this.getIsEventPerformedOnTextInputTarget(e) || isInMonacoEditor || isInModal)
    ) {
      return;
    }

    const shortcut = this.shortcuts.find((x) => x.key === kc);

    if (shortcut) {
      e.preventDefault();
      e.stopPropagation();
      shortcut.execute();
    }
  };

  async reloadScreenList() {
    this.setState({ isScreensLoading: true });
    try {
      this.props.sd.setScreenList();
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isScreensLoading: false });
    }
  }

  loadScreen = async () => {
    const { sd } = this.props;
    const isSelectedScreenIsReportDesign = sd.isReportDesignPageSelected();
    const isComingFromReportDesigner = sd.dashboardState === DashboardState.reportDesigner;

    if (isSelectedScreenIsReportDesign || isComingFromReportDesigner) {
      if (isSelectedScreenIsReportDesign) {
        await sd.saveScreen(true);
      }
      await this.reloadScreenList();

      const { screenList, project } = sd;
      const { lastVisitedScreenId } = project;

      if (lastVisitedScreenId) {
        sd.loadScreen(lastVisitedScreenId, true);
      } else {
        const screenId = screenList.find(
          (x) =>
            x.publicMetaData.screenType !== EScreenType.Report &&
            x.publicMetaData.screenType !== EScreenType.Master &&
            x.publicMetaData.screenType !== EScreenType.Component
        )?.id;
        if (screenId) {
          sd.loadScreen(screenId, true);
        }
      }
    }
  };

  handleCheckSaveWf = async () => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    if (wf) {
      await this.props.sd.saveWorkFlow(wf);
    }
  };

  handleRedirectUIDesign() {
    if (this.props.sd.dashboardState === DashboardState.design) return;

    const redirectToUIDesign = () => {
      this.props.sd.setDashboardState(DashboardState.design);
      this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
      this.loadScreen();

      // this.props.sd.getTicketNotifyCount();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToUIDesign);
      return;
    }

    redirectToUIDesign();
  }

  handleRedirectToDatasources() {
    if (this.props.sd.dashboardState === DashboardState.dataDiagramScheme) return;

    const redirectToDatasources = () => {
      this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToDatasources);
      return;
    }

    redirectToDatasources();
  }

  handleRedirectToWorkflow = async () => {
    if (this.props.sd.dashboardState === DashboardState.workFlow) return;

    // VTO: This constraint is removed via WFEngine architecture
    // if (window?.location.hostname !== "localhost") {
    //   try {
    //     const res = await WorkFlowService.CheckWorkflowInMultipleProjects(this.props.sd.project.id);
    //     if (res === false) {
    //       this.props.showDomainAccessRequiredModal("WorkflowMultipleProjects");
    //       return;
    //     }
    //   } catch (err) {
    //     this.props.showDomainAccessRequiredModal("WorkflowMultipleProjects");
    //     return;
    //   }
    // }

    const redirectToWorkflow = () => {
      if (KMainFunctions.isDomainHasAccess("Workflow") === false) {
        this.props.showDomainAccessRequiredModal("Workflow");
        return;
      }

      this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
      this.props.sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
      if (!this.props.sd.lastOpenWfId) this.props.sd.setCreatingWfActive(true);
      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      if (this.props.sd.lastOpenWfId) this.props.sd.setSelectedWf(this.props.sd.lastOpenWfId);
      this.props.sd.setActiveWorkFlowRightPanel(RightPanelType.Properties);
      // this.props.sd.getTicketNotifyCount();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToWorkflow);
      return;
    }

    redirectToWorkflow();
  };

  handleRedirectToNotificationTemplate() {
    if (this.props.sd.dashboardState === DashboardState.notificationTemplate) return;

    const redirectToNotificationTemplate = () => {
      this.props.sd.setDashboardState(DashboardState.notificationTemplate);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToNotificationTemplate);
      return;
    }

    redirectToNotificationTemplate();
  }

  handleRedirectToReportDesigner() {
    // Report Designer kill-switch: Alt+5 is a no-op when the feature is disabled.
    if (!isReportDesignerEnabled()) return;
    if (this.props.sd.dashboardState === DashboardState.reportDesigner) return;

    const redirectToReportDesigner = () => {
      this.props.sd.enterReportDesigner({ showLanding: true });

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToReportDesigner);
      return;
    }

    redirectToReportDesigner();
  }

  handleRedirectToScheduledJobs() {
    if (this.props.sd.dashboardState === DashboardState.scheduledJobs) return;

    const redirectToScheduledJobs = () => {
      if (KMainFunctions.isDomainHasAccess("Scheduled Jobs") === false) {
        // this.props.showDomainAccessRequiredModal("Scheduled Jobs");
        return;
      }
      this.props.sd.setDashboardState(DashboardState.scheduledJobs);
      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
      this.props.sd.setCreateSJobActive(true);
      this.props.sd.setActiveScheduledJobsPanel(LeftPanelType.None);
      // this.props.sd.getTicketNotifyCount();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToScheduledJobs);
      return;
    }

    redirectToScheduledJobs();
  }

  handleRedirectToAPIBuilder() {
    if (this.props.sd.dashboardState === DashboardState.apiManagement) return;

    const redirectToApiBuilder = () => {
      this.props.sd.setDashboardState(DashboardState.apiManagement);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.props.sd.setActiveApiManagementPanel(LeftPanelType.ApiManagement);
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToApiBuilder);
      return;
    }

    redirectToApiBuilder();
  }

  handleRedirectToConfigurationManager() {
    if (this.props.sd.dashboardState === DashboardState.configManager) return;

    const redirectToConfigurationManager = () => {
      this.props.sd.setDashboardState(DashboardState.configManager);
      this.props.sd.cancelCreatingOrEditingConfiguration();

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToConfigurationManager);
      return;
    }

    redirectToConfigurationManager();
  }

  handleRedirectToHelpDesk() {
    if (this.props.sd.dashboardState === DashboardState.zendesk) return;

    const redirectToHelpDesk = () => {
      this.props.sd.setDashboardState(DashboardState.zendesk);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToHelpDesk);
      return;
    }

    redirectToHelpDesk();
  }

  handleRedirectToRunSettings = () => {
    if (this.props.sd.dashboardState === DashboardState.runSettings) return;

    const redirectToRunSettings = () => {
      this.props.sd.setDashboardState(DashboardState.runSettings);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.handleCheckSaveWf();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi()
    ) {
      this.props.setNextCallbackFunction(redirectToRunSettings);
      return;
    }

    redirectToRunSettings();
  };

  render() {
    return <></>;
  }
}

const mapDispatchToProps = (dispatch) => ({
  showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const shortcutListenerForNavigation = connect(
  null,
  mapDispatchToProps
)(
  withSD<IShortcutListenerForNavigationProps>(
    withHistory<IShortcutListenerForNavigationProps>(ShortcutListenerForNavigation)
  )
);
export { shortcutListenerForNavigation as ShortcutListenerForNavigation };
